/** Мелкие переиспользуемые части карточки макета: поля формы и картинка с фолбэком. */

import React, { useEffect, useState } from 'react'
import { ImageOff } from 'lucide-react'
import { Select } from '../../../../components/ui/Select'
import { cn } from '../../../../lib/cn'
import { AuthorizedMediaImage } from '../../../../shared/media/authorizedMedia'

export const FIELD =
  'w-full min-h-10 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-colors focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100'
export const FIELD_RO =
  'w-full min-h-10 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 dark:border-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-400'
export const FIELD_CHANGED =
  'border-amber-400 bg-amber-50/40 ring-1 ring-amber-300 dark:border-amber-600 dark:bg-amber-950/20 dark:ring-amber-800'
export const SEC =
  'text-2xs font-semibold uppercase tracking-wider text-gray-500 dark:text-zinc-400'

/**
 * Картинка с фолбэком на плейсхолдер при ошибке загрузки.
 *
 * Файлы гейтвея (превью макета, миниатюра файла печати) едут авторизованным
 * запросом; внешние публичные визуалы воронок остаются прямой ссылкой —
 * различает адреса сам AuthorizedMediaImage. Ошибку показываем красным
 * значком: серый квадрат читался бы как «изображения нет».
 */
export const SafeImg: React.FC<{
  src: string
  alt: string
  className?: string
  iconClass?: string
  thumbnailWidth?: number
  errorFallback?: React.ReactNode
  /** Отложенная загрузка по умолчанию включена; выключить там, где картинка одна и заведомо в кадре. */
  lazy?: boolean
}> = ({ src, alt, className, iconClass = 'h-10 w-10', thumbnailWidth, errorFallback, lazy }) => {
  const [failed, setFailed] = useState(false)
  useEffect(() => setFailed(false), [src, thumbnailWidth])
  const fallback = errorFallback ?? (
    <span
      role="img"
      aria-label={`${alt}: не удалось загрузить`}
      title="Не удалось загрузить изображение"
      className="inline-flex items-center justify-center"
    >
      <ImageOff aria-hidden="true" className={cn(iconClass, 'text-red-400 dark:text-red-500')} />
    </span>
  )
  if (failed) return fallback
  return (
    <AuthorizedMediaImage
      src={src}
      alt={alt}
      thumbnailWidth={thumbnailWidth}
      lazy={lazy}
      onError={() => setFailed(true)}
      className={className}
      loadingFallback={<span role="status" aria-label={`Загрузка: ${alt}`} className={cn(iconClass, 'animate-pulse bg-gray-100 dark:bg-zinc-800')} />}
      errorFallback={fallback}
    />
  )
}

const ChangedMark: React.FC<{ shown: boolean }> = ({ shown }) =>
  shown ? (
    <span className="text-2xs font-normal text-amber-600 dark:text-amber-400">изменено</span>
  ) : null

export function Field({
  label,
  value,
  onChange,
  readOnly,
  type = 'text',
  className,
  changed = false,
  hint,
  placeholder,
}: {
  label: string
  value: string
  onChange?: (v: string) => void
  readOnly?: boolean
  type?: React.HTMLInputTypeAttribute
  className?: string
  changed?: boolean
  hint?: string
  placeholder?: string
}) {
  const id = React.useId()
  return (
    <div className={className}>
      <label
        htmlFor={id}
        className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-zinc-300"
      >
        <span>{label}</span>
        <ChangedMark shown={changed} />
      </label>
      <input
        id={id}
        type={type}
        value={value}
        readOnly={readOnly}
        placeholder={placeholder}
        onChange={(e) => onChange?.(e.target.value)}
        className={readOnly ? FIELD_RO : cn(FIELD, changed && FIELD_CHANGED)}
      />
      {hint && <p className="mt-1 text-2xs text-gray-500 dark:text-zinc-400">{hint}</p>}
    </div>
  )
}

export function SelectField({
  label,
  value,
  options,
  onChange,
  className,
  changed = false,
  emptyLabel = 'Не указано',
  optionLabel,
}: {
  label: string
  value: string
  options: string[]
  onChange: (value: string) => void
  className?: string
  changed?: boolean
  emptyLabel?: string
  /** Подпись значения, когда в справочнике лежат слаги, а не готовый текст */
  optionLabel?: (value: string) => string
}) {
  const id = React.useId()
  // Сохранённое значение может отсутствовать в справочнике (старый импорт) —
  // показываем его первым, иначе правка поля молча стёрла бы данные.
  const values = value && !options.includes(value) ? [value, ...options] : options
  return (
    <div className={className}>
      <label
        htmlFor={id}
        className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-zinc-300"
      >
        <span>{label}</span>
        <ChangedMark shown={changed} />
      </label>
      <Select
        id={id}
        value={value}
        onValueChange={onChange}
        className={cn('min-h-10 w-full py-2', changed && FIELD_CHANGED)}
      >
        <option value="">{emptyLabel}</option>
        {values.map((option) => (
          <option key={option} value={option}>
            {optionLabel ? optionLabel(option) : option}
          </option>
        ))}
      </Select>
    </div>
  )
}

export function TextAreaField({
  label,
  value,
  onChange,
  rows = 2,
  changed = false,
  placeholder,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  rows?: number
  changed?: boolean
  placeholder?: string
}) {
  const id = React.useId()
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-zinc-300"
      >
        <span>{label}</span>
        <ChangedMark shown={changed} />
      </label>
      <textarea
        id={id}
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={cn(FIELD, changed && FIELD_CHANGED)}
      />
    </div>
  )
}

/** Пара «подпись — значение» для сводок, где правка не нужна */
export const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="min-w-0">
    <div className="text-2xs uppercase tracking-wide text-gray-400 dark:text-zinc-500">
      {label}
    </div>
    <div className="mt-0.5 truncate text-[0.8125rem] text-gray-900 dark:text-zinc-100">
      {children}
    </div>
  </div>
)
